import * as React from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Menu, X, Facebook, Instagram, MessageCircle, Linkedin, Mail, Calendar, Globe, LogIn, LogOut, User, Settings, ShoppingBag, UserCircle, Gift, ChevronDown, LayoutDashboard, KeyRound, Printer } from "lucide-react"
import { NotificationBell } from "@/components/NotificationBell"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import nomadFestLogo from "@/assets/nomad-fest-logo.png"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

const Navigation = () => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [user, setUser] = React.useState<any>(null)
  const [profile, setProfile] = React.useState<any>(null)
  const [isAdmin, setIsAdmin] = React.useState(false)
  const [hasEventPermissions, setHasEventPermissions] = React.useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  // Close mobile menu on route changes
  React.useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  // Close mobile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const nav = document.querySelector('[data-mobile-nav]')
      const menuButton = document.querySelector('[data-mobile-menu-button]')
      
      if (isOpen && nav && !nav.contains(event.target as Node) && 
          !menuButton?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  React.useEffect(() => {
    // Track latest auth event to prevent a stale getSession() result
    // from overwriting a newer SIGNED_IN state on slow networks / live domain.
    let latestAuthEventAt = 0

    // Register listener BEFORE restoring the session, per Supabase guidance.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      latestAuthEventAt = Date.now()
      setUser(session?.user ?? null)
      if (!session?.user) {
        setProfile(null)
        setIsAdmin(false)
        setHasEventPermissions(false)
      }
    })

    // Restore persisted session from localStorage. getSession() does not hit the
    // network and is safe against the stale-overwrite issue we guard above.
    const startedAt = Date.now()
    supabase.auth.getSession().then(({ data: { session } }) => {
      // If a newer auth event already landed, ignore this restore.
      if (latestAuthEventAt > startedAt) return
      setUser(session?.user ?? null)
      if (!session?.user) {
        setProfile(null)
        setIsAdmin(false)
        setHasEventPermissions(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Run admin / permissions checks separately when the user changes,
  // so async Supabase calls never run inside the auth state callback.
  React.useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    ;(async () => {
      await Promise.all([
        checkAdminStatus(user.id).catch(() => {}),
        checkEventPermissions(user.id).catch(() => {}),
      ])
      if (cancelled) return
    })()
    return () => { cancelled = true }
  }, [user?.id])

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('role, full_name, avatar_url')
        .eq('id', userId)
        .single()

      const { data: adminUser } = await supabase
        .from('admin_users')
        .select('is_super_admin')
        .eq('user_id', userId)
        .single()

      setProfile(profileData)
      setIsAdmin(profileData?.role === 'admin' || adminUser?.is_super_admin === true)
    } catch (error) {
      console.error('Error checking admin status:', error)
      setIsAdmin(false)
    }
  }

  const checkEventPermissions = async (userId: string) => {
    try {
      // Check if user has any entity_permissions or has created any events
      const { data: permissions } = await supabase
        .from('entity_permissions')
        .select('id')
        .eq('user_id', userId)
        .limit(1)

      if (permissions && permissions.length > 0) {
        setHasEventPermissions(true)
        return
      }

      // Also check if user created any events
      const tables = ['festivals', 'academies', 'retreats', 'workations']
      for (const table of tables) {
        const { data } = await supabase
          .from(table as any)
          .select('id')
          .eq('created_by', userId)
          .limit(1)
        
        if (data && data.length > 0) {
          setHasEventPermissions(true)
          return
        }
      }
      
      setHasEventPermissions(false)
    } catch (error) {
      console.error('Error checking event permissions:', error)
      setHasEventPermissions(false)
    }
  }

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      toast.error('Error signing out')
    } else {
      toast.success('Signed out successfully')
      navigate('/')
    }
  }

  const isActive = (path: string) => location.pathname === path

  const navItems = [
    { href: "/", label: "Home" },
    { label: "Events", subItems: [
        { href: "/our-events", label: "Our Events", icon: Calendar },
        { href: "/nomad-fests-worldwide", label: "Nomad Fests Worldwide", icon: Globe },
        { href: "/events", label: "All Events", icon: Calendar },
    ] },
    // Community is hidden for now (no community yet). To RE-ENABLE, uncomment
    // the line below (direct link), or restore the dropdown:
    //   { label: "Community", subItems: [
    //       { href: "/community/local", label: "Local Communities", icon: MapPin },
    //       { href: "/community/global", label: "Global Community", icon: Globe },
    //   ] },
    // { href: "/community/local", label: "Community" },
    { href: "/marketplace", label: "Marketplace" },
    { href: "/partners", label: "Partners" },
    { href: "/awards", label: "Awards" },
    { href: "/blog", label: "Blog" },
    { href: "/contact", label: "Contact" }
  ]

  // Lock body scroll when mobile menu is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[600] pt-4 px-4">
      {/* Floating pill navigation */}
      <nav className={cn(
        "max-w-5xl mx-auto bg-background/90 backdrop-blur-xl border border-border/60",
        "shadow-[0_8px_40px_rgba(0,0,0,0.10)]",
        isOpen ? "rounded-[2rem] rounded-b-none" : "rounded-full"
      )}>
        <div className="px-5 lg:px-6">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src={nomadFestLogo}
                alt="Nomad Fest Logo"
                className="h-6 w-auto"
                width="63"
                height="24"
                loading="eager"
                data-nav-logo
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
              {navItems.map((item) =>
                'subItems' in item && Array.isArray((item as any).subItems) ? (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "text-sm font-medium transition-all px-3 py-1.5 rounded-full flex items-center gap-1",
                          ((item as any).subItems as Array<{ href: string }>).some((s) => location.pathname.startsWith(s.href))
                            ? "bg-primary/10 text-primary font-semibold"
                            : "text-foreground/80 hover:text-foreground hover:bg-muted"
                        )}
                      >
                        {item.label}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="z-[750] bg-background rounded-2xl p-1">
                      {((item as any).subItems as Array<{ href: string; label: string; icon?: any }>).map((sub) => (
                        <DropdownMenuItem key={sub.href} onClick={() => navigate(sub.href)} className="cursor-pointer rounded-xl">
                          {sub.icon && <sub.icon className="mr-2 h-4 w-4" />}
                          {sub.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                <Link
                  key={item.href}
                  to={item.href!}
                  className={cn(
                    "text-sm font-medium transition-all px-3 py-1.5 rounded-full",
                    isActive(item.href!)
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-foreground/80 hover:text-foreground hover:bg-muted"
                  )}
                >
                  {item.label}
                </Link>
                )
              )}
              {isAdmin && (
                <Link
                  to="/admin"
                  className={cn(
                    "text-sm font-medium transition-all px-3 py-1.5 rounded-full flex items-center gap-1",
                    isActive('/admin')
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-foreground/80 hover:text-foreground hover:bg-muted"
                  )}
                >
                  <Settings className="h-4 w-4" />
                  Admin
                </Link>
              )}
            </div>

            {/* Right side - User Menu & Social */}
            <div className="hidden lg:flex items-center space-x-2 flex-shrink-0">
              {user ? (
                <>
                  <NotificationBell />
                  {/* User Dropdown Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="rounded-full flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={profile?.avatar_url} alt={profile?.full_name || user?.email} />
                          <AvatarFallback>
                            {profile?.full_name ? profile.full_name.charAt(0).toUpperCase() : 
                             user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 z-[750] bg-background">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium leading-none">{profile?.full_name || 'User'}</p>
                          <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/my-events')}>
                        <Calendar className="mr-2 h-4 w-4" />
                        <span>My Events</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/my-orders')}>
                        <ShoppingBag className="mr-2 h-4 w-4" />
                        <span>Orders</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/profile')}>
                        <UserCircle className="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </DropdownMenuItem>
                      {hasEventPermissions && (
                        <DropdownMenuItem onClick={() => navigate('/organizer-dashboard')}>
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          <span>Organizer Dashboard</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => navigate('/messages')}>
                        <MessageCircle className="mr-2 h-4 w-4" />
                        <span>Messages</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/community/local')}>
                        <User className="mr-2 h-4 w-4" />
                        <span>Community Hub</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/auth?change-password=true')}>
                        <KeyRound className="mr-2 h-4 w-4" />
                        <span>Change Password</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/print/overview')}>
                        <Printer className="mr-2 h-4 w-4" />
                        <span>Print Sheets (A3)</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut}>
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Sign Out</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 font-medium"
                >
                  GET ON BOARD
                </Button>
              )}
              <a 
                href="https://www.instagram.com/nomad_fest_community" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground/80 hover:text-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(!isOpen)}
                data-mobile-menu-button
              >
                {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden bg-background border-t border-border rounded-b-3xl z-[700] relative" data-mobile-nav>
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navItems.map((item) => 
                'subItems' in item && Array.isArray((item as any).subItems) ? (
                  <div key={item.label} className="space-y-1">
                    <span className="block px-3 py-2 text-base font-medium text-muted-foreground">
                      {item.label}
                    </span>
                    {((item as any).subItems as Array<{ href: string; label: string; icon?: any }>).map((sub) => (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        className={cn(
                          "block px-3 py-2 pl-6 rounded-md text-sm font-medium transition-colors flex items-center gap-2",
                          isActive(sub.href)
                            ? "text-primary bg-primary/10"
                            : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        {sub.icon && <sub.icon className="h-4 w-4" />}
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                <Link
                  key={item.href}
                  to={item.href!}
                  className={cn(
                    "block px-3 py-2 rounded-md text-base font-medium transition-colors",
                    isActive(item.href!)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </Link>
                )
              )}
              {isAdmin && (
                <Link
                  to="/admin"
                  className={cn(
                    "block px-3 py-2 rounded-md text-base font-medium transition-colors flex items-center gap-2",
                    isActive('/admin')
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  Admin Dashboard
                </Link>
              )}
              <div className="px-3 py-2 space-y-2">
                {user ? (
                  <>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        navigate('/my-events');
                        setIsOpen(false);
                      }}
                    >
                      My Events
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/my-orders');
                        setIsOpen(false);
                      }}
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      My Orders
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/profile');
                        setIsOpen(false);
                      }}
                    >
                      <UserCircle className="h-4 w-4 mr-2" />
                      Profile
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate('/rewards');
                        setIsOpen(false);
                      }}
                    >
                      <Gift className="h-4 w-4 mr-2" />
                      Rewards
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleSignOut}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => navigate('/auth')}
                    className="w-full rounded-full"
                  >
                    GET ON BOARD
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </div>
  )
}

export { Navigation }